import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Plus, ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import RegulatoryAttachments, { useRegulatoryAttachments } from "./RegulatoryAttachments";
import {
  DevicePicker, Field, ModuleHeader, NoProjectDevices, ReviewStatus, SectionCard,
  SelectField, TextArea,
} from "./RegulatoryShared";

const EMPTY = {
  intervention: "pending_determination",
  determination_basis: "",
  name: "",
  number: "",
  contact: "",
  email: "",
  website: "",
  notes: "",
  certificates: [],
  review_status: "pending",
};

const STATUS_OPTIONS = ["pending", "review", "issues"];

function newCertificate() {
  return {
    id: globalThis.crypto?.randomUUID?.() || `cert-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: "",
    number: "",
    scope: "",
    covered_device_ids: [],
    issued_at: "",
    expires_at: "",
    scope_review: "pending",
    scope_review_notes: "",
  };
}

export default function NotifiedBodyTab({ project, devices = [], onChange }) {
  const t = useT();
  const [deviceId, setDeviceId] = useState(devices[0]?.id || "");
  const device = devices.find((item) => item.id === deviceId);
  const existing = device?.notified_body || {};
  const persistedCertificateIds = new Set((existing.certificates || []).map((certificate) => certificate.id));
  const [draft, setDraft] = useState({ ...EMPTY, ...existing, certificates: existing.certificates || [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const attachments = useRegulatoryAttachments(project.id, deviceId, "notified_body", onChange);

  useEffect(() => {
    if (!devices.some((item) => item.id === deviceId)) setDeviceId(devices[0]?.id || "");
  }, [devices, deviceId]);

  useEffect(() => {
    const current = devices.find((item) => item.id === deviceId)?.notified_body || {};
    setDraft({ ...EMPTY, ...current, certificates: current.certificates || [] });
    setError("");
  }, [deviceId, devices]);

  const alerts = useMemo(() => {
    if (!device) return [];
    const result = [];
    if (draft.intervention === "pending_determination") result.push({ type: "pending", text: t("reg.nb.alert_determine") });
    if (draft.intervention === "not_required" && !draft.determination_basis.trim()) {
      result.push({ type: "pending", text: t("reg.nb.alert_basis") });
    }
    if (draft.intervention === "required") {
      if (!draft.name.trim()) result.push({ type: "pending", text: t("reg.nb.alert_name") });
      if (!draft.number.trim()) result.push({ type: "pending", text: t("reg.nb.alert_number") });
      if (!draft.certificates.length) result.push({ type: "pending", text: t("reg.nb.alert_certificate") });
      draft.certificates.forEach((certificate) => {
        const label = certificate.number || certificate.name || t("reg.nb.certificate");
        if (!certificate.number.trim() || !certificate.scope.trim()) {
          result.push({ type: "pending", text: `${t("reg.nb.alert_cert_details")}: ${label}` });
        }
        if (!certificate.issued_at) {
          result.push({ type: "pending", text: `${t("reg.nb.alert_issue_date")}: ${label}` });
        }
        if (!certificate.covered_device_ids.includes(deviceId)) {
          result.push({ type: "issue", text: `${t("reg.nb.alert_not_covered")}: ${label}` });
        }
        if (certificate.scope_review === "not_coherent") {
          result.push({ type: "issue", text: `${t("reg.nb.alert_scope_issue")}: ${label}` });
        } else if (certificate.scope_review !== "coherent") {
          result.push({ type: "pending", text: `${t("reg.nb.alert_scope_review")}: ${label}` });
        }
        if (!attachments.documents.some((doc) => doc.record_id === `certificate:${certificate.id}` && doc.review_status === "reviewed")) {
          result.push({ type: "pending", text: `${t("reg.nb.alert_source_doc")}: ${label}` });
        }
        if (certificate.expires_at) {
          const daysLeft = Math.ceil((new Date(`${certificate.expires_at}T00:00:00`) - new Date()) / 86400000);
          if (daysLeft < 0) result.push({ type: "issue", text: `${t("reg.nb.alert_expired")}: ${label} (${certificate.expires_at})` });
          else if (daysLeft <= 60) result.push({ type: "warning", text: `${t("reg.nb.alert_expiring")}: ${label} (${daysLeft} ${t("reg.nb.days")})` });
        }
      });
    }
    return result;
  }, [attachments.documents, device, deviceId, draft, t]);

  const shownStatus = alerts.some((alert) => alert.type === "issue") ? "issues" : draft.review_status;

  function update(patch) {
    setDraft((current) => ({
      ...current,
      ...patch,
      review_status: current.review_status === "reviewed" ? "review" : (patch.review_status || current.review_status),
    }));
  }

  function updateCertificate(id, patch) {
    setDraft((current) => ({
      ...current,
      review_status: current.review_status === "reviewed" ? "review" : current.review_status,
      certificates: current.certificates.map((certificate) =>
        certificate.id === id ? { ...certificate, ...patch } : certificate
      ),
    }));
  }

  async function save(reviewStatus = draft.review_status) {
    if (!device) return;
    setBusy(true);
    setError("");
    try {
      const payload = { ...draft, review_status: reviewStatus };
      const { data } = await api.put(`/projects/${project.id}/devices/${device.id}/notified-body`, payload);
      setDraft({ ...EMPTY, ...data, certificates: data.certificates || [] });
      await onChange?.();
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function validityMessage(certificate) {
    if (!certificate.expires_at) return t("reg.nb.validity_not_set");
    const daysLeft = Math.ceil((new Date(`${certificate.expires_at}T00:00:00`) - new Date()) / 86400000);
    if (daysLeft < 0) return t("reg.nb.expired");
    if (daysLeft <= 60) return t("reg.nb.expiring_soon");
    return t("reg.nb.date_recorded");
  }

  if (!devices.length) return <NoProjectDevices projectId={project.id} />;
  if (!device) return null;

  return (
    <div className="space-y-4" data-testid="notified-body-module">
      <ModuleHeader
        icon={ShieldCheck}
        title={t("reg.nb.title")}
        subtitle={t("reg.nb.subtitle")}
        status={shownStatus}
      />

      <SectionCard title={t("reg.device_context")} subtitle={t("reg.nb.context_note")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DevicePicker devices={devices} value={deviceId} onChange={setDeviceId} testid="nb-device-picker" />
          <div className="flex items-end justify-end">
            <span className="text-xs text-zinc-500">{device.mdr_class ? `${t("dev.class")} ${device.mdr_class}` : t("reg.class_missing")}</span>
          </div>
        </div>
      </SectionCard>

      <SectionCard title={t("reg.nb.intervention_title")} subtitle={t("reg.nb.intervention_help")} testid="nb-intervention-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectField
            label={t("reg.nb.intervention")}
            value={draft.intervention}
            onChange={(value) => update({ intervention: value })}
            testid="nb-intervention-select"
            options={[
              ["pending_determination", t("reg.nb.intervention_pending")],
              ["required", t("reg.nb.intervention_required")],
              ["not_required", t("reg.nb.intervention_not_required")],
            ]}
          />
          {draft.intervention === "not_required" && (
            <TextArea label={t("reg.nb.determination_basis")} value={draft.determination_basis}
              onChange={(value) => update({ determination_basis: value })} rows={2} testid="nb-determination-basis" />
          )}
        </div>
        {draft.intervention === "required" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <Field label={t("reg.nb.name")} value={draft.name} onChange={(value) => update({ name: value })} testid="nb-name" />
            <Field label={t("reg.nb.number")} value={draft.number} onChange={(value) => update({ number: value })} testid="nb-number" />
            <Field label={t("reg.nb.contact")} value={draft.contact} onChange={(value) => update({ contact: value })} testid="nb-contact" />
            <Field label={t("reg.nb.email")} value={draft.email} onChange={(value) => update({ email: value })} type="email" testid="nb-email" />
            <Field label={t("reg.nb.website")} value={draft.website} onChange={(value) => update({ website: value })} testid="nb-website" />
            <TextArea label={t("reg.nb.notes")} value={draft.notes} onChange={(value) => update({ notes: value })} rows={2} testid="nb-notes" />
          </div>
        )}
      </SectionCard>

      {draft.intervention === "required" && (
        <SectionCard title={t("reg.nb.certificates_title")} subtitle={t("reg.nb.certificates_help")} testid="nb-certificates-section">
          <div className="space-y-4">
            {draft.certificates.map((certificate, index) => {
              const recordId = `certificate:${certificate.id}`;
              return (
                <div key={certificate.id} className="border border-zinc-200 rounded-sm p-4 space-y-4" data-testid={`nb-certificate-${index}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                      {t("reg.nb.certificate")} {index + 1}
                    </div>
                    <button type="button" onClick={() => update({
                      certificates: draft.certificates.filter((item) => item.id !== certificate.id),
                    })} className="text-xs text-red-700 hover:underline" data-testid={`nb-delete-certificate-${index}`}>
                      {t("reg.delete")}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label={t("reg.nb.certificate_name")} value={certificate.name}
                      onChange={(value) => updateCertificate(certificate.id, { name: value })} testid={`nb-cert-name-${index}`} />
                    <Field label={t("reg.nb.certificate_number")} value={certificate.number}
                      onChange={(value) => updateCertificate(certificate.id, { number: value })} testid={`nb-cert-number-${index}`} />
                    <Field label={t("reg.nb.issued")} type="date" value={certificate.issued_at}
                      onChange={(value) => updateCertificate(certificate.id, { issued_at: value })} testid={`nb-cert-issued-${index}`} />
                    <Field label={t("reg.nb.expires")} type="date" value={certificate.expires_at}
                      onChange={(value) => updateCertificate(certificate.id, { expires_at: value })} testid={`nb-cert-expires-${index}`} />
                    <TextArea label={t("reg.nb.scope")} value={certificate.scope}
                      onChange={(value) => updateCertificate(certificate.id, { scope: value })} rows={3} testid={`nb-cert-scope-${index}`} />
                    <div>
                      <SelectField label={t("reg.nb.scope_review")}
                        value={certificate.scope_review}
                        onChange={(value) => updateCertificate(certificate.id, { scope_review: value })}
                        testid={`nb-cert-scope-review-${index}`}
                        options={[
                          ["pending", t("reg.nb.scope_pending")],
                          ["coherent", t("reg.nb.scope_coherent")],
                          ["not_coherent", t("reg.nb.scope_not_coherent")],
                        ]} />
                      <div className="text-[10px] text-zinc-500 mt-2">{validityMessage(certificate)}</div>
                    </div>
                    <TextArea label={t("reg.nb.scope_review_notes")} value={certificate.scope_review_notes}
                      onChange={(value) => updateCertificate(certificate.id, { scope_review_notes: value })} rows={2} testid={`nb-cert-scope-notes-${index}`} />
                  </div>
                  <div className="border-t border-zinc-100 pt-3">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mb-2">{t("reg.nb.covered_devices")}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {devices.map((candidate) => (
                        <label key={candidate.id} className="inline-flex items-start gap-2 text-xs text-zinc-700">
                          <input type="checkbox" checked={certificate.covered_device_ids.includes(candidate.id)}
                            onChange={(event) => {
                              const nextIds = event.target.checked
                                ? [...new Set([...certificate.covered_device_ids, candidate.id])]
                                : certificate.covered_device_ids.filter((id) => id !== candidate.id);
                              updateCertificate(certificate.id, { covered_device_ids: nextIds });
                            }} data-testid={`nb-cert-device-${index}-${candidate.id}`} />
                          <span>{candidate.name}{candidate.model ? ` · ${candidate.model}` : ""}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {persistedCertificateIds.has(certificate.id) ? (
                    <RegulatoryAttachments {...attachments} error="" recordId={recordId} />
                  ) : (
                    <div className="text-xs text-zinc-500">{t("reg.nb.save_before_upload")}</div>
                  )}
                </div>
              );
            })}
            <button type="button" onClick={() => update({ certificates: [...draft.certificates, newCertificate()] })}
              className="inline-flex items-center gap-1 border border-blue-200 bg-blue-50 px-3 py-2 rounded-sm text-xs font-semibold text-blue-800"
              data-testid="nb-add-certificate">
              <Plus size={13} weight="bold" /> {t("reg.nb.add_certificate")}
            </button>
          </div>
        </SectionCard>
      )}

      {alerts.length > 0 && (
        <div className={`grid-card p-4 border-l-4 ${alerts.some((item) => item.type === "issue") ? "border-l-red-500" : "border-l-amber-500"}`}
          data-testid="nb-alerts">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-700 mb-2">
            <WarningCircle size={15} weight="fill" /> {t("reg.pending_items")}
          </div>
          <ul className="space-y-1">
            {alerts.map((item, index) => (
              <li key={`${item.text}-${index}`} className={`text-xs ${item.type === "issue" ? "text-red-700" : item.type === "warning" ? "text-amber-700" : "text-zinc-600"}`}>
                · {item.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <div className="text-sm text-red-700" role="alert" data-testid="nb-error">{error}</div>}

      <div className="grid-card p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <SelectField label={t("reg.review_status")} value={draft.review_status}
          onChange={(value) => update({ review_status: value })}
          testid="nb-review-status"
          options={STATUS_OPTIONS.map((status) => [status, t(`reg.status.${status}`)])} />
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => save()} disabled={busy}
            className="border border-zinc-200 bg-white px-4 py-2 rounded-sm text-xs font-semibold disabled:opacity-40"
            data-testid="nb-save">{busy ? t("reg.saving") : t("reg.save")}</button>
          <button type="button" onClick={() => save("reviewed")} disabled={busy || shownStatus === "issues"}
            className="btn-primary px-4 py-2 text-xs disabled:opacity-40" data-testid="nb-mark-reviewed">
            {t("reg.mark_reviewed")}
          </button>
        </div>
      </div>
      {attachments.error && <div className="text-xs text-red-700" role="alert">{attachments.error}</div>}
      <div className="text-[10px] text-zinc-500">{t("reg.nb.disclaimer")}</div>
    </div>
  );
}
